import { SPKPositionStatusSwitch } from '$components/Positions/SPKCandidatesPositionPage/SPKPositionStatusSwitch/SPKPositionStatusSwitch';
import SPKCandidateHiddenSkills from '$components/SPKCandidateList/SPKCandidateCard/SPKCandidateHiddenSkills';
import ActiveValues from '$constants/ActiveValues';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { ReactComponent as Copy } from '$icons/copy.svg';
import { ReactComponent as Search } from '$icons/SearchIcon.svg';
import { ReactComponent as Arrow } from '$icons/uncolored/arrow.svg';
import { useTranslation } from 'react-i18next';
import { isValidNumber } from '$utils/isValidNumber';
import copyToClipboard from '$utils/copyToClipboard';
import SPKToast from '$components/SPKToast/SPKToast';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import getUserType from '$utils/getUserType';
import { UserTypes } from '$types/UserTypes';
import ShareCurrencyModal from '$components/Share/ShareCurrencyModal/ShareCurrencyModal';

type SPKPositionBarProps = {
  isLoadingPosition: boolean;
  candidatesPositionCount: Partial<Record<ActiveValues, number>>;
  position: PositionInternalDto;
  cps: ActiveValues;
  showInterestedToEmployer: boolean;
};

const SPKPositionBar = ({
  position,
  isLoadingPosition,
  candidatesPositionCount,
  cps,
  showInterestedToEmployer = false,
}: SPKPositionBarProps) => {
  const { currency, currencyCode, usdToCurrencyConverter } = useAppSelector(
    (state) => state.brand
  );
  const userType = getUserType();
  const isInternal = userType === UserTypes.INTERNAL;
  const MAX_DISPLAYED_SKILLS = 4;
  const [showDescription, setShowDescription] = useState(false);
  const [showShareCurrencyModal, setShowShareCurrencyModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const lastSearchQuery = useRef('');
  const [t] = useTranslation('global');
  const { control, watch, handleSubmit } = useForm({
    defaultValues: {
      searchInput:
        searchParams.get('Query') ||
        searchParams.get('Id') ||
        searchParams.get('Email') ||
        '',
    },
  });

  const searchValue = watch('searchInput');

  const handleSearch = useCallback(
    (val: { search: string }) => {
      const params = new URLSearchParams(searchParams);
      params.delete('Email');
      params.delete('Id');
      params.delete('Query');
      if (isValidNumber(val.search)) {
        params.set('Id', val.search);
      } else if (val.search.includes('@')) {
        params.set('Email', val.search);
      } else if (val.search) {
        params.set('Query', val.search);
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const copyIdHandler = async (selectedCurrencyCode: string) => {
    const baseUrl = window.location.origin;
    const sharePositionURL = new URL(
      `${baseUrl}/share/position/${position?.ShareReferenceId}`
    );
    sharePositionURL.searchParams.set('currency', selectedCurrencyCode);
    await copyToClipboard(sharePositionURL.toString());
    SPKToast(t('PositionCard.CopyShareLink'));
  };

  const submitSearchHandler = () => {
    if (lastSearchQuery.current.trim() !== searchValue.trim()) {
      handleSearch({ search: searchValue });
      lastSearchQuery.current = searchValue;
    }
  };

  useEffect(() => {
    if (lastSearchQuery.current.trim() !== searchValue.trim()) {
      const resetQuery = setTimeout(() => {
        handleSearch({ search: searchValue });
        lastSearchQuery.current = searchValue;
      }, 1000);

      return () => clearTimeout(resetQuery);
    }

    return () => {};
  }, [handleSearch, searchValue]);

  if (isLoadingPosition || !position) return null;

  return (
    <>
      <div className="flex flex-col lg:flex-row mb-2 w-full">
        <div className="flex flex-col items-start">
          <div className="flex flex-row items-center gap-2 text-spk-mediumContrast text-xs">
            <Link to="/employer-dashboard/positions">
              {t('PositionCard.Positions')}
            </Link>
            <span className="text-sm">&gt;</span>
            <span>{`${
              position?.CustomJobTitle || position?.JobPosition?.title
            }`}</span>
          </div>
          <button
            type="button"
            className="flex flex-row gap-4 items-center"
            onClick={() => setShowDescription((prev) => !prev)}
          >
            <span className="text-spk-highContrast font-bold text-xl text-left">{`${
              position?.CustomJobTitle || position?.JobPosition?.title
            }`}</span>
            <Arrow
              className={`fill-employer-primary w-4 h-4 transition-all duration-150 ${
                showDescription ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </button>
          <button
            type="button"
            className="flex flex-row items-center gap-2 mt-1"
            onClick={() =>
              isInternal
                ? setShowShareCurrencyModal(true)
                : copyIdHandler(currencyCode)
            }
          >
            <span className="text-sm">ID: {position.Id}</span>
            <Copy className="h-4 w-4" />
          </button>
          {isInternal && (
            <ShareCurrencyModal
              isOpen={showShareCurrencyModal}
              closeModal={() => setShowShareCurrencyModal(false)}
              onConfirm={copyIdHandler}
            />
          )}
        </div>
        <div className="flex flex-row ml-auto items-center max-w-full">
          <div className="ml-auto flex flex-row bg-white items-center gap-2 px-2 rounded-xl border border-employer-secondary text-spk-mediumContrast h-12">
            <Search />
            <form onSubmit={handleSubmit(submitSearchHandler)}>
              <fieldset>
                <Controller
                  control={control}
                  name="searchInput"
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="h-full outline-none placeholder:text-spk-mediumContrast max-w-44 w-full"
                      placeholder="Search"
                      autoComplete="off"
                    />
                  )}
                />
              </fieldset>
            </form>
          </div>
          <div className="border-l border-employer-secondary ml-4 pl-4 flex flex-row items-center self-center overflow-hidden max-w-full">
            <SPKPositionStatusSwitch
              value={(cps as ActiveValues) ?? ActiveValues.New}
              candidatesCount={candidatesPositionCount}
              showInterestedToEmployer={showInterestedToEmployer}
            />
          </div>
        </div>
      </div>
      <div
        className={`grid text-sm w-full transition-all duration-300 ${
          showDescription ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-y-3 py-2">
            {/* Row 1: internal-only admin fields */}
            {isInternal && (
              <div className="flex flex-row divide-x divide-employer-secondary *:px-2 -mx-2 flex-wrap gap-y-2">
                {position?.Company?.Name && (
                  <div className="flex gap-2">
                    <span className="text-spk-mediumContrast">
                      {t('PositionCard.Company')}:
                    </span>
                    <span className="text-spk-highContrast">
                      {position.Company.Name}
                    </span>
                  </div>
                )}
                {position?.Tenant?.Name && (
                  <div className="flex gap-2">
                    <span className="text-spk-mediumContrast">
                      {t('PositionCard.Tenant')}:
                    </span>
                    <span className="text-spk-highContrast">
                      {position.Tenant.Name}
                    </span>
                  </div>
                )}
                {position?.DealId && (
                  <div className="flex gap-2">
                    <span className="text-spk-mediumContrast">
                      {t('PositionCard.DealId')}:
                    </span>
                    <span className="text-spk-highContrast">
                      {position.DealId}
                    </span>
                  </div>
                )}
                {position?.EnableSmartInterviews != null && (
                  <div className="flex gap-2">
                    <span className="text-spk-mediumContrast">
                      {t('PositionCard.EnableSmartInterviews')}:
                    </span>
                    <span className="text-spk-highContrast">
                      {position.EnableSmartInterviews ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
                {position?.IsPublic != null && (
                  <div className="flex gap-2">
                    <span className="text-spk-mediumContrast">
                      {t('PositionCard.IsPublic')}:
                    </span>
                    <span className="text-spk-highContrast">
                      {position.IsPublic ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
              </div>
            )}
            {/* Row 2: shared fields (visible to employer & internal) */}
            <div className="flex flex-row divide-x divide-employer-secondary *:px-2 -mx-2 flex-wrap gap-y-2">
              <div className="flex gap-2">
                <span className="text-spk-mediumContrast">
                  {t('PositionCard.Experience')}
                </span>
                <span className="text-spk-highContrast">
                  {position?.ExperienceYears} {t('PositionCard.year')}
                  {position?.ExperienceYears &&
                    position?.ExperienceYears > 1 &&
                    's'}
                </span>
              </div>
              <div className="flex gap-2">
                <span className="text-spk-mediumContrast">
                  {t('PositionCard.MonthlyBudget', { currencyCode })}:{' '}
                </span>
                <span className="text-spk-highContrast">
                  {`${currency}${usdToCurrencyConverter(
                    position?.SalaryLowerBound
                  )} - ${currency}${usdToCurrencyConverter(
                    position?.SalaryUpperBound
                  )}`}
                </span>
              </div>
              {position?.TypeOfWork && (
                <div className="flex gap-2">
                  <span className="text-spk-mediumContrast">
                    {t('PositionCard.TypeOfWork')}:
                  </span>
                  <span className="text-spk-highContrast">
                    {position.TypeOfWork}
                  </span>
                </div>
              )}
              {position?.SeniorityLevel && (
                <div className="flex gap-2">
                  <span className="text-spk-mediumContrast">
                    {t('PositionCard.Seniority')}:
                  </span>
                  <span className="text-spk-highContrast">
                    {position.SeniorityLevel}
                  </span>
                </div>
              )}
              {position?.WorkLocation && (
                <div className="flex gap-2">
                  <span className="text-spk-mediumContrast">
                    {t('PositionCard.WorkType')}:
                  </span>
                  <span className="text-spk-highContrast">
                    {position.WorkLocation}
                  </span>
                </div>
              )}
              {position?.DegreeType && (
                <div className="flex gap-2">
                  <span className="text-spk-mediumContrast">
                    {t('PositionCard.DegreeType')}:
                  </span>
                  <span className="text-spk-highContrast">
                    {position.DegreeType}
                  </span>
                </div>
              )}
              {position?.DegreeName && (
                <div className="flex gap-2">
                  <span className="text-spk-mediumContrast">
                    {t('PositionCard.DegreeName')}:
                  </span>
                  <span className="text-spk-highContrast">
                    {position.DegreeName}
                  </span>
                </div>
              )}
            </div>

            {/* Row 2: internal-only fields */}
            {isInternal && (
              <div className="flex flex-row divide-x divide-employer-secondary *:px-2 -mx-2 flex-wrap gap-y-2">
                {position?.Country?.NiceName && (
                  <div className="flex gap-2">
                    <span className="text-spk-mediumContrast">
                      {t('PositionCard.Country')}:
                    </span>
                    <span className="text-spk-highContrast">
                      {position.Country.NiceName}
                    </span>
                  </div>
                )}
                {position?.PositionLanguages &&
                  position.PositionLanguages.length > 0 && (
                    <div className="flex gap-2">
                      <span className="text-spk-mediumContrast">
                        {t('PositionCard.Languages')}:
                      </span>
                      <span className="text-spk-highContrast">
                        {position.PositionLanguages.map(
                          (pl) => pl.Language.Name
                        ).join(', ')}
                      </span>
                    </div>
                  )}
                {position?.IdealStartDate && (
                  <div className="flex gap-2">
                    <span className="text-spk-mediumContrast">
                      {t('PositionCard.IdealStartDate')}:
                    </span>
                    <span className="text-spk-highContrast">
                      {position.IdealStartDate}
                    </span>
                  </div>
                )}
                {position?.WorkingHours && (
                  <div className="flex gap-2">
                    <span className="text-spk-mediumContrast">
                      {t('PositionCard.WorkingHours')}:
                    </span>
                    <span className="text-spk-highContrast">
                      {position.WorkingHours}
                    </span>
                  </div>
                )}
                {position?.WorkingDays && (
                  <div className="flex gap-2">
                    <span className="text-spk-mediumContrast">
                      {t('PositionCard.WorkingDays')}:
                    </span>
                    <span className="text-spk-highContrast">
                      {position.WorkingDays}
                    </span>
                  </div>
                )}
                {position?.BonusDetails && (
                  <div className="flex gap-2">
                    <span className="text-spk-mediumContrast">
                      {t('PositionCard.Bonus')}:
                    </span>
                    <span className="text-spk-highContrast">
                      {position.BonusDetails}
                    </span>
                  </div>
                )}
                {position?.CommissionDetails && (
                  <div className="flex gap-2">
                    <span className="text-spk-mediumContrast">
                      {t('PositionCard.Commission')}:
                    </span>
                    <span className="text-spk-highContrast">
                      {position.CommissionDetails}
                    </span>
                  </div>
                )}
                {position?.BenefitsDetails && (
                  <div className="flex gap-2">
                    <span className="text-spk-mediumContrast">
                      {t('PositionCard.Benefits')}:
                    </span>
                    <span className="text-spk-highContrast">
                      {position.BenefitsDetails}
                    </span>
                  </div>
                )}
                {position?.OtherCompensation && (
                  <div className="flex gap-2">
                    <span className="text-spk-mediumContrast">
                      {t('PositionCard.Other')}:
                    </span>
                    <span className="text-spk-highContrast">
                      {position.OtherCompensation}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Required Skills */}
            <div className="flex flex-row items-center flex-wrap gap-2">
              <span className="text-spk-mediumContrast">
                {t('PositionCard.SkillsetOverview')}:
              </span>
              <div className="flex flex-row gap-2 items-center">
                {position?.Skills_position?.slice(0, MAX_DISPLAYED_SKILLS)?.map(
                  (item) => (
                    <span
                      key={`positionSkill_${item.Id}`}
                      className="rounded text-spk-mediumContrast border border-employer-secondary p-1 text-xs bp"
                    >
                      {item.Title}
                    </span>
                  )
                )}
                {position?.Skills_position &&
                  position?.Skills_position.length > MAX_DISPLAYED_SKILLS && (
                    <SPKCandidateHiddenSkills
                      hiddenSkills={position?.Skills_position?.slice(
                        MAX_DISPLAYED_SKILLS,
                        -1
                      )?.map((singleSkill) => ({
                        Id: singleSkill.Id,
                        Skill: { title: singleSkill.Title },
                      }))}
                      uniqueId={position.Id}
                    />
                  )}
              </div>
            </div>

            {/* Job Objective */}
            {position?.JobDescription && (
              <div className="flex flex-col gap-1">
                <span className="text-spk-mediumContrast">
                  {t('PositionCard.JobObjective')}:
                </span>
                <div
                  className="text-spk-highContrast [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:ml-1"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: position.JobDescription }}
                />
              </div>
            )}

            {/* Responsibilities (internal only) */}
            {isInternal && position?.Responsibilities && (
              <div className="flex flex-col gap-1 pb-2">
                <span className="text-spk-mediumContrast">
                  {t('PositionCard.Responsibilities')}:
                </span>
                <div
                  className="text-spk-highContrast [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:ml-1"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: position.Responsibilities,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SPKPositionBar;
